export const DEFAULT_REGION_CODE = "+852";

export type CountryCodeOption = { value: string; flag: string };

const onlyDigits = (value: unknown) => String(value ?? "").replace(/\D/g, "");

/**
 * Parse a stored phone value into a region code + local number.
 *
 * Supports both full values (`+852 1234 5678`, `+85212345678`) and legacy
 * local-only HK values (`12345678`). The longest matching country code wins,
 * so a shorter entry can never shadow `+852`. Anything without a known prefix
 * is treated as a legacy HK number.
 */
export const splitPhoneNumber = (
  storedPhone: string | number | null | undefined,
  countryCodeList: CountryCodeOption[],
): { regionCode: string; localNumber: string } => {
  const stored = String(storedPhone ?? "").trim();
  if (!stored) {
    return { regionCode: DEFAULT_REGION_CODE, localNumber: "" };
  }

  const match = [...countryCodeList]
    .sort((a, b) => b.value.length - a.value.length)
    .find((country) => stored.startsWith(country.value));
  if (match) {
    return {
      regionCode: match.value,
      localNumber: onlyDigits(stored.slice(match.value.length)),
    };
  }

  return {
    regionCode: DEFAULT_REGION_CODE,
    localNumber: onlyDigits(stored),
  };
};

/** Build the display/storage value, e.g. `+852 12345678`. */
export const formatPhoneNumber = (
  regionCode: string,
  localNumber: string | number | null | undefined,
): string => {
  const local = onlyDigits(localNumber);
  const region = String(regionCode ?? "").trim() || DEFAULT_REGION_CODE;
  return local ? `${region} ${local}` : "";
};

/**
 * Canonical form used only for "did the user change the number?" checks.
 * A legacy local-only HK value and its `+852` form compare equal.
 */
export const canonicalPhoneNumber = (
  storedPhone: string | number | null | undefined,
  countryCodeList: CountryCodeOption[],
): string => {
  const { regionCode, localNumber } = splitPhoneNumber(
    storedPhone,
    countryCodeList,
  );
  return localNumber ? `${regionCode}${localNumber}` : "";
};

export const isSamePhoneNumber = (
  first: string | number | null | undefined,
  second: string | number | null | undefined,
  countryCodeList: CountryCodeOption[],
): boolean =>
  canonicalPhoneNumber(first, countryCodeList) ===
  canonicalPhoneNumber(second, countryCodeList);

/**
 * Client-side format check mirroring the server's validator: Hong Kong is
 * exactly 8 local digits, other regions use the lenient 6-15 digit rule.
 * Returns an i18n key on failure, `null` when valid.
 */
export const validatePhoneNumber = (
  regionCode: string,
  localNumber: string,
): string | null => {
  const digits = onlyDigits(localNumber);
  if (!digits) return "signup.phoneRequired";
  if (String(regionCode ?? "").trim() === DEFAULT_REGION_CODE) {
    return digits.length === 8 ? null : "signup.hkPhoneMustBe8Digits";
  }
  return digits.length >= 6 && digits.length <= 15
    ? null
    : "signup.invalidPhone";
};
